/**
 * ActivityCard - Afișează activitatea medicului (login, timp activ, etc.)
 */

import React from 'react'
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  CircularProgress,
  Divider,
} from '@mui/material'
import {
  AccessTime as AccessTimeIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material'
import { formatSeconds } from '../../hooks/useAnalytics'

interface ActivityCardProps {
  loginCount: number
  totalLoggedTimeSec: number
  totalActiveTimeSec: number
  idleTimeSec: number
  activeSessionsCount: number
  peakHour?: number
  lastActivityAt?: string
  loading?: boolean
}

export const ActivityCard: React.FC<ActivityCardProps> = ({
  loginCount,
  totalLoggedTimeSec,
  totalActiveTimeSec,
  idleTimeSec,
  activeSessionsCount,
  peakHour,
  lastActivityAt,
  loading = false,
}) => {
  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    )
  }

  const activePercentage = totalLoggedTimeSec > 0 
    ? (totalActiveTimeSec / totalLoggedTimeSec) * 100 
    : 0

  return (
    <Card elevation={1} sx={{ height: '100%' }}>
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, height: '100%', overflow: 'auto' }}>
        {/* Header - Compact */}
        <Box display="flex" alignItems="center" mb={1}>
          <AccessTimeIcon color="primary" sx={{ mr: 0.5, fontSize: 18 }} />
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Activitate</Typography>
        </Box>

        {/* Stats principale - Compact */}
        <Grid container spacing={1} mb={1}>
          <Grid item xs={6}>
            <Box textAlign="center">
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: 'primary.main' }}>{loginCount}</Typography>
              <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Login-uri</Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box textAlign="center">
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: 'success.main' }}>{activeSessionsCount}</Typography>
              <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Sesiuni Active</Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 1 }} />

        {/* Timp Lucrat - Compact */}
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, mb: 0.5 }}>Timp Lucrat:</Typography>
        <Box mb={1}>
          <Box display="flex" justifyContent="space-between" mb={0.25}>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Total logat:</Typography>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{formatSeconds(totalLoggedTimeSec)}</Typography>
          </Box>
          <Box display="flex" justifyContent="space-between" mb={0.25}>
            <Typography sx={{ fontSize: '0.72rem', color: 'success.main' }}>Timp activ:</Typography>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'success.main' }}>{formatSeconds(totalActiveTimeSec)}</Typography>
          </Box>
          <Box display="flex" justifyContent="space-between" mb={0.25}>
            <Typography sx={{ fontSize: '0.72rem', color: 'warning.main' }}>Inactiv:</Typography>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'warning.main' }}>{formatSeconds(idleTimeSec)}</Typography>
          </Box>
          <Box display="flex" justifyContent="space-between">
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Eficiență:</Typography>
            <Chip label={`${activePercentage.toFixed(0)}%`} color={activePercentage >= 80 ? 'success' : activePercentage >= 60 ? 'warning' : 'error'} size="small" sx={{ height: 18, fontSize: '0.68rem' }} />
          </Box>
        </Box>

        {/* Peak Hour - Compact */}
        {peakHour !== undefined && (
          <>
            <Divider sx={{ my: 1 }} />
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}><ScheduleIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.25 }} />Oră vârf:</Typography>
              <Chip label={`${peakHour}:00 - ${peakHour + 1}:00`} color="info" size="small" sx={{ height: 18, fontSize: '0.68rem' }} />
            </Box>
          </>
        )}

        {/* Last Activity - Compact */}
        {lastActivityAt && (
          <Box mt={0.5} display="flex" justifyContent="space-between">
            <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Ultima activitate:</Typography>
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 500 }}>{new Date(lastActivityAt).toLocaleString('ro-RO')}</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

